import type { App } from '../app/App';
import { t } from '../i18n';
import { modal } from './components';
import { h } from './dom';

/**
 * Terms, privacy and cookie consent: asked once per device before anything
 * else (and again whenever CONSENT_VERSION changes). Both boxes are required:
 * the game cannot run without saving to this browser. The accepted version
 * is also recorded on the server with the account.
 */

/** The date the terms/privacy/cookie notice last changed. Bump it to ask everyone again. */
export const CONSENT_VERSION = '2026-10-21';
const KEY = 'paper-legends/consent';

export function consentGiven(): boolean {
  try {
    const raw = localStorage.getItem(KEY);
    return raw !== null && (JSON.parse(raw) as { v?: string }).v === CONSENT_VERSION;
  } catch {
    return false;
  }
}

function remember(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ v: CONSENT_VERSION, at: Date.now() }));
  } catch {
    /* private mode: asked again next time */
  }
}

/** Shows the agreement until accepted. Resolves at once when already accepted on this device. */
export async function ensureConsent(app: App): Promise<void> {
  if (consentGiven()) return;
  const box = (label: (Node | string)[]): { row: HTMLElement; input: HTMLInputElement } => {
    const input = h('input', { type: 'checkbox', class: 'consent-check' }) as HTMLInputElement;
    return { row: h('label', { class: 'consent-row' }, input, h('span', null, ...label)), input };
  };
  const link = (href: string, text: string): HTMLElement => h('a', { href, target: '_blank', rel: 'noopener' }, text);
  const terms = box([t('I have read and accept the '), link('/terms.html', t('Terms of Use')), t(' and the '), link('/privacy.html', t('Privacy Policy')), ' ', h('b', null, t('(required)'))]);
  const cookies = box([t('I allow the game to store the cookies and browser data it needs: my saved notebook, sign-in and security.'), ' ', h('b', null, t('(required)'))]);
  const hint = h('p', { class: 'consent-hint', 'aria-live': 'polite' });
  const body = h(
    'div',
    { class: 'consent-body' },
    h('p', null, t('Before you play, please read and accept these. They explain what the game keeps and why.')),
    h(
      'ul',
      { class: 'consent-facts' },
      h('li', null, t('Your notebook is saved in this browser and, once you have an account, on the game server.')),
      h('li', null, t('We use no advertising or tracking cookies. Google sign-in and Stripe payments use their own cookies only when you choose them.')),
      h('li', null, t('Under 13? Ask a parent or guardian to read this and tick the boxes with you.')),
    ),
    terms.row,
    cookies.row,
    hint,
  );
  let accept: HTMLButtonElement | null = null;
  const sync = (): void => {
    const ok = terms.input.checked && cookies.input.checked;
    accept?.classList.toggle('is-disabled', !ok);
    accept?.setAttribute('aria-disabled', String(!ok));
    if (ok) hint.textContent = '';
  };
  terms.input.addEventListener('change', sync);
  cookies.input.addEventListener('change', sync);
  await modal(t('Game agreement'), body, [{ label: t('Accept and play'), value: 'ok', variant: 'primary', color: 'green' }], {
    dismissable: false,
    className: 'consent-card',
    accept: () => {
      if (terms.input.checked && cookies.input.checked) return true;
      hint.textContent = t('Tick both boxes to continue. The game cannot save without them.');
      return false;
    },
    onOpen: () => {
      accept = body.closest('.modal-card')?.querySelector<HTMLButtonElement>('.modal-actions button') ?? null;
      sync();
    },
  });
  remember();
  // An account made before this version: record the new acceptance with it.
  if (app.store.state.online) void app.api.raw('POST', '/api/consent', { version: CONSENT_VERSION }).catch(() => {});
}
